import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_WIDTH = 450;
const DEFAULT_HEIGHT = 300;

function parsePixelSize(raw: string | null, fallback: number): number {
  const n = Number.parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, 160), 2400);
}

function iframePathFromQuery(pathParam: string | null): string {
  const raw = (pathParam || '/pay').trim() || '/pay';
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) {
    return '/pay';
  }
  const path = raw.split('?')[0];
  if (path === '/embedded' || path.startsWith('/embedded/')) {
    return '/pay';
  }
  return path;
}

/**
 * Host page that loads vaiu.app (this origin) inside a sized iframe
 * so embed checkout can be tested without a merchant site.
 *
 *   /embedded
 *   /embedded?width=450&height=300
 *   /embedded?width=400&height=640&amount=1&currency=CHF&autoPay=true
 */
const EmbeddedTestPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const width = parsePixelSize(searchParams.get('width'), DEFAULT_WIDTH);
  const height = parsePixelSize(searchParams.get('height'), DEFAULT_HEIGHT);
  const iframeSrc = useMemo(() => {
    const iframeParams = new URLSearchParams(searchParams);
    iframeParams.delete('width');
    iframeParams.delete('height');
    iframeParams.delete('path');
    iframeParams.set('embed', 'true');
    if (!iframeParams.has('postToParent') && !iframeParams.has('ptp')) {
      iframeParams.set('postToParent', 'true');
    }
    const path = iframePathFromQuery(searchParams.get('path'));
    const qs = iframeParams.toString();
    return `${window.location.origin}${path}${qs ? `?${qs}` : ''}`;
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6 text-slate-900">
      <div className="mx-auto max-w-4xl space-y-4">
        <div>
          <h1 className="text-lg font-semibold">Embedded vaiu.app</h1>
          <p className="mt-1 text-sm text-slate-600">
            iframe {width}×{height}px. Pass <code className="rounded bg-slate-200 px-1">width</code> and{' '}
            <code className="rounded bg-slate-200 px-1">height</code> as query params. Other params are forwarded
            to <code className="rounded bg-slate-200 px-1">/pay?embed=true</code>.
          </p>
        </div>
        <div
          className="overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm"
          style={{ width, height, maxWidth: '100%' }}
        >
          <iframe
            title="Embedded vaiu.app"
            src={iframeSrc}
            width={width}
            height={height}
            className="h-full w-full border-0"
            allow="payment *; clipboard-write"
          />
        </div>
        <p className="break-all font-mono text-xs text-slate-500">{iframeSrc}</p>
      </div>
    </div>
  );
};

export default EmbeddedTestPage;
